import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SUBJECTS, Subject } from '../../../data/academicTaxonomy';

interface SubjectComboboxProps {
  value: string;
  onChange: (next: string) => void;
  options?: ReadonlyArray<Subject>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

interface DisplayRow {
  type: 'divider' | 'item';
  letter?: string;
  subject?: Subject;
}

const SubjectCombobox: React.FC<SubjectComboboxProps> = ({
  value,
  onChange,
  options = SUBJECTS,
  placeholder = 'Select a subject…',
  disabled = false,
  required = false,
  id,
  name,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Alphabetically sorted; filtered by query if present.
  const rows = useMemo<DisplayRow[]>(() => {
    const q = query.trim().toLowerCase();

    const filtered = q
      ? [...options]
          .filter((s) => s.name.toLowerCase().includes(q))
          .sort((a, b) => {
            const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
            const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return a.name.localeCompare(b.name);
          })
      : [...options].sort((a, b) => a.name.localeCompare(b.name));

    const result: DisplayRow[] = [];
    let lastLetter = '';
    for (const s of filtered) {
      const letter = s.name.charAt(0).toUpperCase();
      if (letter !== lastLetter) {
        result.push({ type: 'divider', letter });
        lastLetter = letter;
      }
      result.push({ type: 'item', subject: s });
    }
    return result;
  }, [options, query]);

  const itemIndices = useMemo(
    () => rows.map((r, i) => (r.type === 'item' ? i : -1)).filter((i) => i >= 0),
    [rows]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Reset query and active row when closing
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectSubject = (subject: Subject) => {
    onChange(subject.name);
    setOpen(false);
    setQuery('');
  };

  const clearSelection = () => {
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const moveActive = (direction: 1 | -1) => {
    if (itemIndices.length === 0) return;
    const currentPos = itemIndices.indexOf(activeIndex);
    let nextPos = currentPos + direction;
    if (nextPos < 0) nextPos = itemIndices.length - 1;
    if (nextPos >= itemIndices.length) nextPos = 0;
    setActiveIndex(itemIndices[nextPos]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0) {
        e.preventDefault();
        const row = rows[activeIndex];
        if (row?.subject) selectSubject(row.subject);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex items-center gap-2 w-full rounded-lg border px-3 py-2 transition-colors',
          'focus-within:ring-2 focus-within:ring-cambridge',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          'border-timberwolf'
        )}
        style={{ backgroundColor: disabled ? '#F3F4F6' : '#F9FAFB' }}
      >
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id || 'subject'}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          required={required}
          disabled={disabled}
          value={open ? query : value}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent outline-none text-sm placeholder-gray-400"
          style={{ color: 'var(--twc-ink)' }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={clearSelection}
            aria-label="Clear selection"
            className="p-0.5 rounded hover:bg-gray-100"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform flex-shrink-0',
            open && 'rotate-180'
          )}
        />
      </div>

      {open && (
        <ul
          ref={listRef}
          id={`${id || 'subject'}-listbox`}
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border bg-white shadow-lg"
          style={{ borderColor: 'var(--twc-timberwolf)' }}
        >
          {rows.length === 0 ? (
            <li className="px-3 py-4 text-sm text-gray-500 text-center">
              No subjects found
            </li>
          ) : (
            rows.map((row, index) => {
              if (row.type === 'divider') {
                return (
                  <li
                    key={`div-${row.letter}`}
                    data-row-index={index}
                    aria-hidden="true"
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 select-none"
                  >
                    <span className="flex-1 h-px bg-gray-200" />
                    <span>{row.letter}</span>
                    <span className="flex-1 h-px bg-gray-200" />
                  </li>
                );
              }
              const isActive = index === activeIndex;
              const isSelected = row.subject!.name === value;
              return (
                <li
                  key={row.subject!.name}
                  role="option"
                  aria-selected={isSelected}
                  data-row-index={index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectSubject(row.subject!);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={cn(
                    'cursor-pointer px-3 py-2 text-sm flex items-center justify-between gap-2',
                    isActive && 'bg-cambridge/10',
                    isSelected && 'font-medium'
                  )}
                  style={{ color: 'var(--twc-ink)' }}
                >
                  <span className="truncate">{row.subject!.name}</span>
                  <span
                    className="text-[11px] text-gray-400 flex-shrink-0 truncate max-w-[40%]"
                    title={row.subject!.category}
                  >
                    {row.subject!.category}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default SubjectCombobox;
